import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { BottomNav } from '@/components/BottomNav';
import { SettingsModal } from '@/components/SettingsModal';
import { useUserPreferences, usePrograms, useStoreActions, usePreferencesStore } from '@/store/useStore';
import { Search, Bell, BookOpen, User, MapPin, History, TrendingUp, X, Settings, LogOut, Calendar, Clock, AlertCircle, CheckCircle, Info, Moon, Sun } from 'lucide-react';
import ChatAssistant from './ChatAssistant';
import MobileInstallBanner from '@/components/MobileInstallBanner';
import OfflineIndicator from '@/components/OfflineIndicator';
import { NotificationPermissionPrompt } from '@/components/NotificationPermissionPrompt';
import HomePage from './HomePageWrapper';
import PersonalTimetable from './PersonalTimetable';
import BrowseTimetables from './BrowseTimetables';
import CampusMap from './CampusMap';
import BusSchedules from './BusSchedules';
import OwletAI from './OwletAI';
import AcademicCalendar from './AcademicCalendar';
import ExamTimetable from './ExamTimetable';
import Feedback from './Feedback';
import Attendance from './Attendance';
import LostAndFound from './LostAndFound';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { DataProcessor } from '@/utils/dataProcessor';
import { findUserProgram } from '@/utils/programMatching';
import { useClassReminders } from '@/hooks/useClassReminders';
import { DashboardProvider } from '@/contexts/DashboardContext';

export default function DashboardLayout() {
  const userPreferences = useUserPreferences();
  const programs = usePrograms();
  const { initializeData, clearUserPreferences } = useStoreActions();
  const { theme, toggleTheme } = usePreferencesStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isOwletPage = location.pathname.startsWith('/dashboard/owlet');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [readNotifications, setReadNotifications] = useState<Set<string>>(new Set());
  const [dismissedNotifications, setDismissedNotifications] = useState<Set<string>>(new Set());
  const [notificationKey, setNotificationKey] = useState(0); // Force re-render key
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);
  const [recentGlobalSearches, setRecentGlobalSearches] = useState<string[]>([]);
  const [searchType, setSearchType] = useState<'all' | 'courses' | 'teachers' | 'rooms'>('all');
  const [searchActiveIndex, setSearchActiveIndex] = useState(-1);
  const [showChat, setShowChat] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Draggable Owlet state - always use absolute positioning from top-left
  const [owletPosition, setOwletPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [owletInitialized, setOwletInitialized] = useState(false);
  const [showOwletTooltip, setShowOwletTooltip] = useState(false);
  const owletRef = useRef<HTMLButtonElement>(null);

  // Initialize class reminders
  useClassReminders();

  // Show tooltip on every session
  useEffect(() => {
    if (owletInitialized && !showChat) {
      // Show tooltip after a short delay
      const showTimer = setTimeout(() => {
        setShowOwletTooltip(true);

        // Hide tooltip after 4 seconds
        const hideTimer = setTimeout(() => {
          setShowOwletTooltip(false);
        }, 4000);

        return () => clearTimeout(hideTimer);
      }, 1500);

      return () => clearTimeout(showTimer);
    }
  }, [owletInitialized, showChat]);

  // Initialize owlet position on mount and handle resize
  useEffect(() => {
    const initOwletPosition = () => {
      const iconSize = 56; // w-14 = 56px
      const padding = 16;
      const bottomNavHeight = window.innerWidth < 1024 ? 80 : 24; // Account for bottom nav on mobile

      // Always start at default position (bottom-right corner)
      setOwletPosition({
        x: window.innerWidth - iconSize - padding,
        y: window.innerHeight - iconSize - bottomNavHeight
      });
      setOwletInitialized(true);
    };

    initOwletPosition();
    window.addEventListener('resize', initOwletPosition);
    return () => window.removeEventListener('resize', initOwletPosition);
  }, []);

  // Owlet drag handlers
  const handleOwletMouseDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // Only prevent default if event is cancelable to avoid passive listener errors
    if (e.cancelable) {
      e.preventDefault();
    }
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    setIsDragging(true);
    setDragStart({ x: clientX - owletPosition.x, y: clientY - owletPosition.y });
  }, [owletPosition]);

  const handleOwletMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDragging) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const newX = clientX - dragStart.x;
    const newY = clientY - dragStart.y;

    // Keep Y within viewport bounds, X can be anywhere during drag
    const iconSize = 56;
    const padding = 8;
    const bottomNavHeight = window.innerWidth < 1024 ? 80 : 24;
    const maxY = window.innerHeight - iconSize - bottomNavHeight;
    const boundedY = Math.max(padding + 60, Math.min(newY, maxY)); // 60px from top for header

    setOwletPosition({ x: newX, y: boundedY });
  }, [isDragging, dragStart]);

  const handleOwletMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);

      // Snap to left or right edge based on position
      const iconSize = 56;
      const padding = 16;
      const screenMiddle = window.innerWidth / 2;

      const snappedX = owletPosition.x < screenMiddle - iconSize / 2
        ? padding // Snap to left
        : window.innerWidth - iconSize - padding; // Snap to right

      setOwletPosition(prev => ({ ...prev, x: snappedX }));
    }
  }, [isDragging, owletPosition.x]);

  // Add/remove drag event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleOwletMove);
      window.addEventListener('mouseup', handleOwletMouseUp);
      window.addEventListener('touchmove', handleOwletMove);
      window.addEventListener('touchend', handleOwletMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleOwletMove);
      window.removeEventListener('mouseup', handleOwletMouseUp);
      window.removeEventListener('touchmove', handleOwletMove);
      window.removeEventListener('touchend', handleOwletMouseUp);
    };
  }, [isDragging, handleOwletMove, handleOwletMouseUp]);

  // Force a dark page background while in the dashboard so the old light theme
  // never shows through (overscroll bounce, edges). Restored when leaving.
  useEffect(() => {
    const prevHtml = document.documentElement.style.background;
    const prevBody = document.body.style.background;
    document.documentElement.style.background = '#0A0A0A';
    document.body.style.background = '#0A0A0A';
    return () => {
      document.documentElement.style.background = prevHtml;
      document.body.style.background = prevBody;
    };
  }, []);

  useEffect(() => {
    initializeData();
    // Load recent searches from localStorage
    const saved = localStorage.getItem('recentGlobalSearches');
    if (saved) {
      setRecentGlobalSearches(JSON.parse(saved));
    }
    // Load read notifications from localStorage
    const readNotifs = localStorage.getItem('readNotifications');
    if (readNotifs) {
      setReadNotifications(new Set(JSON.parse(readNotifs)));
    }

    // Load dismissed notifications from localStorage
    const dismissedNotifs = localStorage.getItem('dismissedNotifications');
    if (dismissedNotifs) {
      setDismissedNotifications(new Set(JSON.parse(dismissedNotifs)));
    }
  }, [initializeData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // CMD/Ctrl + K for search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
      // CMD/Ctrl + , for settings
      if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault();
        alert('Settings page coming soon!');
      }
      // Escape to close any open panels
      if (e.key === 'Escape') {
        setShowSearch(false);
        setShowNotifications(false);
        setShowUserMenu(false);
      }
      // Quick navigation shortcuts
      if ((e.metaKey || e.ctrlKey) && e.key === '1') {
        e.preventDefault();
        navigate('/dashboard/home');
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '2') {
        e.preventDefault();
        navigate('/dashboard/personal');
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '3') {
        e.preventDefault();
        navigate('/dashboard/browse');
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '4') {
        e.preventDefault();
        navigate('/dashboard/rooms');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Define callback functions first (before useEffect)
  const generateSearchSuggestions = useCallback((query: string) => {
    if (!programs || programs.length === 0) return;

    const dataProcessor = DataProcessor.getInstance();
    const suggestions: any[] = [];
    const queryLower = query.toLowerCase();

    // Add quick suggestions based on query
    const allTeachers = dataProcessor.getTeachers();
    const allCourses = dataProcessor.getCourses();
    const allRooms = dataProcessor.getAllRooms();

    // Teacher suggestions
    allTeachers.filter(t => t.toLowerCase().includes(queryLower))
      .slice(0, 2)
      .forEach(teacher => suggestions.push({
        type: 'teacher',
        text: teacher,
        icon: 'user'
      }));

    // Course suggestions  
    allCourses.filter(c => c.toLowerCase().includes(queryLower))
      .slice(0, 2)
      .forEach(course => suggestions.push({
        type: 'course',
        text: course,
        icon: 'book'
      }));

    // Room suggestions
    allRooms.filter(r => r.name.toLowerCase().includes(queryLower))
      .slice(0, 2)
      .forEach(room => suggestions.push({
        type: 'room',
        text: room.name,
        icon: 'map'
      }));

    setSearchSuggestions(suggestions);
  }, [programs]);

  const handleSearchSelect = (searchText: string, type?: string) => {
    // Add to recent searches
    const newRecent = [searchText, ...recentGlobalSearches.filter(s => s !== searchText)].slice(0, 5);
    setRecentGlobalSearches(newRecent);
    localStorage.setItem('recentGlobalSearches', JSON.stringify(newRecent));

    // Navigate to browse with the search/filter
    navigate('/dashboard/browse', {
      state: {
        searchQuery: searchText,
        type: type || 'general',
        applyAsFilter: true
      }
    });
    setShowSearch(false);
    setSearchQuery('');
  };

  const clearRecentGlobalSearches = () => {
    setRecentGlobalSearches([]);
    localStorage.removeItem('recentGlobalSearches');
  };

  const generateNotifications = useCallback(() => {
    if (!programs || programs.length === 0 || !userPreferences) return [];

    const notifications: any[] = [];

    // Add Owlet AI Assistant announcement
    const isOwletDismissed = dismissedNotifications.has('owlet-ai-launch-dec2025');

    if (!isOwletDismissed) {
      const owletNotification = {
        id: 'owlet-ai-launch-dec2025',
        type: 'info',
        title: '🦉 Meet Owlet - Your AI Assistant!',
        message: 'Say hello to Owlet! Your new AI-powered university assistant is here. Ask about your classes, exam dates, bus routes, and more. Just tap the owl icon in the corner to start chatting. Owlet knows your personalized schedule and is ready to help 24/7!',
        time: 'New',
        icon: 'info',
        priority: 'high',
        timestamp: new Date()
      };
      notifications.push(owletNotification);
    }
    const now = new Date();
    const today = now.toLocaleDateString('en-US', { weekday: 'long' });
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const userProgram = findUserProgram(programs, userPreferences);

    if (!userProgram) return [];

    // Load cancelled class notifications - filter by student's program
    const allCancelledClasses = JSON.parse(localStorage.getItem('cancelledClasses') || '[]');
    const studentNotifications = JSON.parse(localStorage.getItem('studentNotifications') || '[]');

    // Filter notifications to only show those for this student's program
    const relevantNotifications = studentNotifications.filter((notif: any) => {
      // Check if the cancelled class belongs to the student's program
      return allCancelledClasses.some((classId: string) =>
        classId.startsWith(`${userProgram.id}-`) &&
        notif.classId === classId
      );
    });

    relevantNotifications.forEach((notif: any) => {
      notifications.push({
        ...notif,
        timestamp: new Date(notif.timestamp),
        priority: 'high',
        isNew: (now.getTime() - new Date(notif.timestamp).getTime()) < 3600000 // New if less than 1 hour old
      });
    });

    const todaySchedule = userProgram.schedule[today] || [];

    // Upcoming classes today
    todaySchedule.forEach((course: any) => {
      if (course.time) {
        const [startTime] = course.time.split('-');
        const [hours, minutes] = startTime.trim().split(':').map(Number);
        const classTime = hours * 60 + minutes;
        const timeDiff = classTime - currentTime;

        // Notify about classes starting within next 60 minutes
        if (timeDiff > 0 && timeDiff <= 60) {
          notifications.push({
            id: `upcoming-${course.course_name}-${course.time}`,
            type: 'upcoming',
            title: 'Upcoming Class',
            message: `${course.course_name || course.course} starts in ${timeDiff} minutes`,
            course: course.course_name || course.course,
            instructor: course.instructor || course.teacher,
            location: course.location,
            time: course.time,
            timestamp: new Date(),
            priority: 'high',
            isNew: true
          });
        }
      }
    });

    // Drop dismissed notifications (makes both per-item dismiss and Clear All
    // actually hide generated items), then sort newest-first, then by priority.
    return notifications
      .filter((n) => !dismissedNotifications.has(n.id))
      .sort((a, b) => {
        const timeA = new Date(a.timestamp).getTime();
        const timeB = new Date(b.timestamp).getTime();
        if (timeB !== timeA) return timeB - timeA; // Newest first

        const priorityOrder: { [key: string]: number } = { high: 0, medium: 1, low: 2 };
        return (priorityOrder[a.priority] || 999) - (priorityOrder[b.priority] || 999);
      });
  }, [programs, userPreferences, dismissedNotifications, notificationKey]);

  const markNotificationsAsRead = useCallback(() => {
    const notifications = generateNotifications();
    const notificationIds = notifications.map(n => n.id);
    const newReadNotifications = new Set([...Array.from(readNotifications), ...notificationIds]);
    setReadNotifications(newReadNotifications);
    localStorage.setItem('readNotifications', JSON.stringify(Array.from(newReadNotifications)));

    // DON'T mark update notification as seen - just mark as read
    // This keeps the notification visible but removes the counter
  }, [generateNotifications, readNotifications]);

  const handleLogout = () => {
    // Clear all user data
    localStorage.removeItem('userPreferences');
    localStorage.removeItem('university-timetable-storage');

    // Clear Zustand store
    clearUserPreferences();

    // Navigate to landing page and force reload
    navigate('/', { replace: true });
    window.location.reload();
  };

  const handleSettingsClick = () => {
    setShowSettings(true);
    setShowUserMenu(false);
  };

  const performSearch = useCallback((query: string, type: typeof searchType) => {
    if (!programs || programs.length === 0) return;

    const dataProcessor = DataProcessor.getInstance();
    const results: any[] = [];
    const searchLower = query.toLowerCase().trim();
    const searchWords = searchLower.split(/\s+/);
    const programsData = dataProcessor.getAllPrograms();

    if (type === 'all' || type === 'courses') {
      // First check for exact course matches (priority)
      programsData.forEach(program => {
        Object.values(program.schedule).forEach(courses => {
          courses.forEach((course: any) => {
            const courseName = course.course_name || course.course || '';
            const courseNameLower = courseName.toLowerCase();

            // Exact match gets priority
            if (courseNameLower === searchLower) {
              results.push({
                type: 'course',
                name: courseName,
                instructor: course.instructor || course.teacher,
                location: course.location,
                time: course.time,
                program: program.program,
                semester: program.semester,
                priority: 1 // Exact match
              });
            }
            // Partial match
            else if (courseNameLower.includes(searchLower)) {
              results.push({
                type: 'course',
                name: courseName,
                instructor: course.instructor || course.teacher,
                location: course.location,
                time: course.time,
                program: program.program,
                semester: program.semester,
                priority: 2 // Partial match
              });
            }
            // Multi-word search - all words must be present
            else if (searchWords.length > 1 && searchWords.every(word => courseNameLower.includes(word))) {
              results.push({
                type: 'course',
                name: courseName,
                instructor: course.instructor || course.teacher,
                location: course.location,
                time: course.time,
                program: program.program,
                semester: program.semester,
                priority: 3 // Multi-word match
              });
            }
          });
        });
      });
    }

    if (type === 'all' || type === 'teachers') {
      const teachers = dataProcessor.getTeachers();
      teachers.forEach(teacher => {
        const teacherLower = teacher.toLowerCase();

        // Exact match
        if (teacherLower === searchLower) {
          results.push({
            type: 'teacher',
            name: teacher,
            programs: dataProcessor.searchByTeacher(teacher).map(p => p.program),
            priority: 1
          });
        }
        // Smart teacher matching (e.g., 'ali' matches 'Dr. Ali Ahmad')
        else if (teacherLower.includes(searchLower) ||
          searchWords.some(word => word.length > 2 && teacherLower.includes(word))) {
          results.push({
            type: 'teacher',
            name: teacher,
            programs: dataProcessor.searchByTeacher(teacher).map(p => p.program),
            priority: 2
          });
        }
        // Multi-word search for teachers
        else if (searchWords.length > 1 && searchWords.every(word => teacherLower.includes(word))) {
          results.push({
            type: 'teacher',
            name: teacher,
            programs: dataProcessor.searchByTeacher(teacher).map(p => p.program),
            priority: 3
          });
        }
      });
    }

    if (type === 'all' || type === 'rooms') {
      const rooms = dataProcessor.searchRooms(query);
      rooms.forEach(room => {
        const roomNameLower = room.name.toLowerCase();
        let priority = 3;

        if (roomNameLower === searchLower) {
          priority = 1;
        } else if (roomNameLower.includes(searchLower)) {
          priority = 2;
        }

        results.push({
          type: 'room',
          name: room.name,
          location: room.location,
          floor: room.floor,
          roomType: room.type,
          priority: priority
        });
      });
    }

    // Also search in programs themselves
    if (type === 'all') {
      programsData.forEach(program => {
        const programLower = program.program.toLowerCase();
        const departmentLower = program.department.toLowerCase();
        const semesterStr = `semester ${program.semester}`.toLowerCase();
        const groupStr = `group ${program.group}`.toLowerCase();

        // Check if search matches program details
        if (programLower.includes(searchLower) ||
          departmentLower.includes(searchLower) ||
          semesterStr.includes(searchLower) ||
          groupStr.includes(searchLower) ||
          (searchWords.length > 1 && searchWords.every(word => {
            const fullStr = `${programLower} ${departmentLower} ${semesterStr} ${groupStr}`;
            return fullStr.includes(word);
          }))) {

          // Add as program result
          if (!results.some(r => r.type === 'program' && r.name === program.program)) {
            results.push({
              type: 'program',
              name: program.program,
              department: program.department,
              semester: program.semester,
              group: program.group,
              priority: programLower === searchLower ? 1 : 2
            });
          }
        }
      });
    }

    // Remove duplicates and sort by priority
    const uniqueResults = results
      .filter((result, index, self) =>
        index === self.findIndex(r => r.type === result.type && r.name === result.name)
      )
      .sort((a, b) => (a.priority || 999) - (b.priority || 999));

    setSearchResults(uniqueResults);
  }, [programs]);

  // useEffect hooks after all useCallback functions — debounced so we don't
  // recompute the full index on every keystroke.
  useEffect(() => {
    setSearchActiveIndex(-1);
    if (searchQuery.trim().length === 0) {
      setSearchResults([]);
      setSearchSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      performSearch(searchQuery, searchType);
      generateSearchSuggestions(searchQuery);
    }, 120);
    return () => clearTimeout(t);
  }, [searchQuery, searchType, performSearch, generateSearchSuggestions]);

  // Global keyboard shortcuts: Cmd/Ctrl+K opens search, Escape closes any panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowSearch((v) => !v);
        return;
      }
      if (e.key === 'Escape') {
        setShowSearch(false);
        setShowNotifications(false);
        setShowUserMenu(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;

      // Close notifications if clicking outside (but not on the panel itself)
      if (showNotifications && !target.closest('[data-testid="button-notifications"]') && !target.closest('[data-panel="notifications"]')) {
        setShowNotifications(false);
      }

      // Close user menu if clicking outside (but not on the panel itself)
      if (showUserMenu && !target.closest('[data-testid="button-user-menu"]') && !target.closest('[data-panel="user-menu"]')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications, showUserMenu]);

  const handleSearchResultClick = (result: any) => {
    // Add to recent searches if clicking a result
    if (searchQuery.trim()) {
      handleSearchSelect(searchQuery);
    }

    setShowSearch(false);

    // Navigate to browse page with search query based on result type
    if (result.type === 'room') {
      navigate('/dashboard/rooms');
      setSearchQuery('');
    } else if (result.type === 'program') {
      // For program results, navigate to browse with the program name as search
      navigate('/dashboard/browse', {
        state: {
          searchQuery: result.name,
          type: 'program',
          applyAsFilter: true
        }
      });
      setSearchQuery('');
    } else {
      // For other results (teacher, course), navigate with the result name
      navigate('/dashboard/browse', {
        state: {
          searchQuery: result.name || searchQuery,
          type: result.type,
          applyAsFilter: true
        }
      });
      setSearchQuery('');
    }
  };

  // Debug log to see userPreferences
  React.useEffect(() => {
    console.log('DashboardLayout - userPreferences updated at', new Date().toISOString());
    console.log('DashboardLayout - Current userPreferences:', userPreferences);
    console.log('- Name:', userPreferences?.name);
    console.log('- Program:', userPreferences?.program);
    console.log('- Department:', userPreferences?.department);
    console.log('- Semester:', userPreferences?.semester, 'Type:', typeof userPreferences?.semester);
    console.log('- Year:', userPreferences?.year, 'Type:', typeof userPreferences?.year);
    console.log('- Group:', userPreferences?.group);
  }, [userPreferences]);

  if (!userPreferences) {
    navigate('/');
    return null;
  }

  const dashboardContextValue = {
    showSearch, setShowSearch,
    showNotifications, setShowNotifications,
    showUserMenu, setShowUserMenu,
    showSettings, setShowSettings,
    markNotificationsAsRead,
    generateNotifications,
    readNotifications,
  };

  return (
    <DashboardProvider value={dashboardContextValue}>
      {/* Unified Layout - Pages handle their own nav/sidebar + bottom spacing */}
      <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
        <Routes>
          <Route path="home" element={<HomePage />} />
          <Route path="personal" element={<PersonalTimetable />} />
          <Route path="browse" element={<BrowseTimetables />} />
          <Route path="rooms" element={<CampusMap />} />
          <Route path="bus" element={<BusSchedules />} />
          <Route path="calendar" element={<AcademicCalendar />} />
          <Route path="exams" element={<ExamTimetable />} />
          <Route path="owlet" element={<OwletAI />} />
          <Route path="attendance" element={<Attendance />} />
          <Route path="feedback" element={<Feedback />} />
          <Route path="lost-found" element={<LostAndFound />} />
        </Routes>

        {/* Bottom Navigation - Mobile Only */}
        <BottomNav />
      </div>

      {/* Search Panel */}
      {showSearch && (
          <div className="fixed inset-0 z-50" style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }} onClick={() => {
            setShowSearch(false);
            setSearchQuery('');
            setSearchResults([]);
          }}>
            <style>{`
              .gs-chip{padding:7px 14px;border-radius:8px;font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.06em;text-transform:capitalize;white-space:nowrap;cursor:pointer;border:1px solid transparent;background:transparent;color:rgba(255,255,255,.4);transition:.15s}
              .gs-chip.on{background:rgba(20,184,166,.1);border-color:rgba(20,184,166,.22);color:#14B8A6}
              .gs-chip.off:hover{background:rgba(255,255,255,.04);color:rgba(255,255,255,.7)}
              .gs-card{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:12px;padding:11px 13px;cursor:pointer;transition:.15s;width:100%;text-align:left}
              .gs-card:hover{background:rgba(255,255,255,.055);border-color:rgba(255,255,255,.13)}
              .gs-ic{width:34px;height:34px;border-radius:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0;background:rgba(20,184,166,.1);border:1px solid rgba(20,184,166,.2);color:#14B8A6}
              .gs-soft{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:9px;cursor:pointer;transition:.15s;color:#fff}
              .gs-soft:hover{background:rgba(255,255,255,.06);border-color:rgba(255,255,255,.13)}
              .gs-x:hover{background:rgba(255,255,255,.08)!important;color:#fff!important}
            `}</style>
            <div
              onClick={(e) => e.stopPropagation()}
              className="fixed top-2 sm:top-20 left-1/2 -translate-x-1/2 w-[96vw] max-w-2xl rounded-2xl overflow-hidden flex flex-col"
              style={{ background: '#121212', border: '1px solid rgba(255,255,255,.08)', boxShadow: '0 24px 70px rgba(0,0,0,.6)', maxHeight: '90vh', fontFamily: "'Outfit',sans-serif", color: '#fff' }}
            >
              <div className="overflow-y-auto" style={{ padding: 16 }}>
                {/* Search Input */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 12, padding: '0 14px', marginBottom: 14 }}>
                  <Search className="w-4 h-4" style={{ color: '#14B8A6', flexShrink: 0 }} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search courses, teachers, rooms…"
                    style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: '#fff', fontSize: 14, height: 46, fontFamily: "'Outfit',sans-serif" }}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') { e.preventDefault(); setSearchActiveIndex((i) => Math.min(i + 1, searchResults.length - 1)); }
                      else if (e.key === 'ArrowUp') { e.preventDefault(); setSearchActiveIndex((i) => Math.max(i - 1, 0)); }
                      else if (e.key === 'Enter') {
                        if (searchActiveIndex >= 0 && searchResults[searchActiveIndex]) {
                          handleSearchResultClick(searchResults[searchActiveIndex]);
                        } else if (searchQuery.trim()) {
                          navigate('/dashboard/browse', { state: { searchQuery: searchQuery.trim() } });
                          setShowSearch(false);
                          setSearchQuery('');
                        }
                      }
                    }}
                    autoFocus
                  />
                  {searchQuery && (
                    <button className="gs-x" onClick={() => setSearchQuery('')} style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,.5)', cursor: 'pointer', flexShrink: 0, transition: '.15s' }}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                {/* Search Type Filters */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 14, overflowX: 'auto', paddingBottom: 2 }}>
                  {['all', 'courses', 'teachers', 'rooms'].map((type) => (
                    <button key={type} className={`gs-chip ${searchType === type ? 'on' : 'off'}`} onClick={() => setSearchType(type as any)}>
                      {type}
                    </button>
                  ))}
                </div>

                {/* Search Button for direct search */}
                {searchQuery.trim() && (
                  <button
                    className="gs-soft"
                    style={{ width: '100%', marginBottom: 14, padding: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#14B8A6', background: 'rgba(20,184,166,.1)', border: '1px solid rgba(20,184,166,.22)', fontFamily: "'Outfit',sans-serif", fontSize: 13, fontWeight: 500 }}
                    onClick={() => {
                      navigate('/dashboard/browse', { state: { searchQuery: searchQuery.trim() } });
                      setShowSearch(false);
                      setSearchQuery('');
                    }}
                  >
                    <Search className="w-4 h-4" /> Search in Browse Timetables
                  </button>
                )}

                {/* Quick Suggestions */}
                {searchQuery && searchSuggestions.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <p style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.14em', color: 'rgba(255,255,255,.25)', marginBottom: 8 }}>QUICK SUGGESTIONS</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {searchSuggestions.slice(0, 4).map((suggestion, index) => (
                        <button key={index} className="gs-soft" style={{ padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 7, fontSize: 12 }} onClick={() => handleSearchSelect(suggestion.text, suggestion.type)}>
                          {suggestion.icon === 'user' && <User className="w-3 h-3" style={{ color: '#14B8A6' }} />}
                          {suggestion.icon === 'book' && <BookOpen className="w-3 h-3" style={{ color: '#14B8A6' }} />}
                          {suggestion.icon === 'map' && <MapPin className="w-3 h-3" style={{ color: '#14B8A6' }} />}
                          <span style={{ color: 'rgba(255,255,255,.75)' }}>{suggestion.text.length > 20 ? suggestion.text.substring(0, 20) + '…' : suggestion.text}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Searches */}
                {searchQuery.trim() === '' && recentGlobalSearches.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <p style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.14em', color: 'rgba(255,255,255,.25)' }}>RECENT SEARCHES</p>
                      <button style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.06em', color: 'rgba(255,255,255,.35)', background: 'none', border: 'none', cursor: 'pointer' }} onClick={clearRecentGlobalSearches}>
                        CLEAR
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {recentGlobalSearches.map((search, index) => (
                        <button key={index} className="gs-soft" style={{ width: '100%', textAlign: 'left', padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }} onClick={() => handleSearchSelect(search, 'general')}>
                          <History className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,.35)' }} />
                          <span>{search}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search Results */}
                <div style={{ maxHeight: '46vh', overflowY: 'auto' }}>
                  {searchQuery.trim() === '' && recentGlobalSearches.length === 0 ? (
                    <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 12, padding: 16 }}>
                      <p style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.14em', color: 'rgba(255,255,255,.3)', marginBottom: 10 }}>TRY SEARCHING FOR</p>
                      <ul style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: 'rgba(255,255,255,.45)' }}>
                        <li>• Course names (e.g., "Database Systems")</li>
                        <li>• Teacher names (e.g., "Dr. Smith")</li>
                        <li>• Room numbers (e.g., "LR-101")</li>
                      </ul>
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {searchResults.map((result, index) => {
                        const meta =
                          result.type === 'course' ? `${result.instructor} · ${result.location}`
                          : result.type === 'teacher' ? `Teaching ${result.programs.length} program(s)`
                          : result.type === 'room' ? `${result.floor} · ${result.roomType}`
                          : result.type === 'program' ? `${result.department} · Sem ${result.semester}`
                          : '';
                        const ResIcon = result.type === 'course' ? BookOpen : result.type === 'teacher' ? User : result.type === 'room' ? MapPin : TrendingUp;
                        const active = index === searchActiveIndex;
                        return (
                          <button
                            key={index}
                            className="gs-card"
                            ref={(el) => { if (active && el) el.scrollIntoView({ block: 'nearest' }); }}
                            onMouseEnter={() => setSearchActiveIndex(index)}
                            onClick={() => handleSearchResultClick(result)}
                            style={active ? { background: 'rgba(20,184,166,.1)', borderColor: 'rgba(20,184,166,.3)' } : undefined}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                              <div className="gs-ic"><ResIcon className="w-4 h-4" /></div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontSize: 13, fontWeight: 500, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{result.name}</p>
                                <p style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta}</p>
                              </div>
                              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.1em', color: active ? '#14B8A6' : 'rgba(255,255,255,.2)', flexShrink: 0 }}>
                                {result.type.toUpperCase()}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : searchQuery.trim() && (
                    <div style={{ background: 'rgba(255,255,255,.03)', border: '1px dashed rgba(255,255,255,.1)', borderRadius: 12, textAlign: 'center', padding: '28px 20px' }}>
                      <Search className="w-9 h-9" style={{ margin: '0 auto 10px', color: 'rgba(255,255,255,.15)' }} />
                      <p style={{ fontSize: 13, color: 'rgba(255,255,255,.35)' }}>No results for "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Keyboard hint footer */}
              <div className="hidden sm:flex" style={{ alignItems: 'center', gap: 14, padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,.07)', flexShrink: 0 }}>
                {[['↑↓', 'navigate'], ['↵', 'open'], ['esc', 'close']].map(([k, label]) => (
                  <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: 'rgba(255,255,255,.3)' }}>
                    <kbd style={{ padding: '2px 6px', borderRadius: 5, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', color: 'rgba(255,255,255,.55)' }}>{k}</kbd>
                    {label}
                  </span>
                ))}
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: 'rgba(255,255,255,.3)' }}>
                  <kbd style={{ padding: '2px 6px', borderRadius: 5, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', color: 'rgba(255,255,255,.55)' }}>⌘K</kbd>
                  toggle
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Notifications Panel */}
        {showNotifications && (
          <div className="fixed inset-0 sm:inset-auto sm:top-16 sm:right-4 z-50">
            {/* Mobile backdrop */}
            <div className="fixed inset-0 sm:hidden" style={{ background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(4px)' }} onClick={() => setShowNotifications(false)} />

            <div
              data-panel="notifications"
              className="fixed bottom-0 left-0 right-0 sm:relative sm:bottom-auto sm:left-auto sm:right-auto w-full sm:w-96 rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
              style={{ background: '#121212', border: '1px solid rgba(255,255,255,.08)', boxShadow: '0 16px 50px rgba(0,0,0,.55)', maxHeight: '82vh', fontFamily: "'Outfit',sans-serif", color: '#fff', paddingBottom: 'env(safe-area-inset-bottom,0px)' }}
            >
              <div style={{ padding: '16px 18px', borderBottom: '1px solid rgba(255,255,255,.07)', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: (() => generateNotifications().length > 0)() ? 12 : 0 }}>
                  <h3 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 20, fontWeight: 400, letterSpacing: '-.01em' }}>Notifications</h3>
                  <button
                    onClick={() => setShowNotifications(false)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.09)'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.05)'; e.currentTarget.style.color = 'rgba(255,255,255,.5)'; }}
                    style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,.5)', cursor: 'pointer', transition: '.15s' }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {(() => {
                  const notifications = generateNotifications();
                  const hasNotifications = notifications.length > 0;
                  return hasNotifications && (
                    <button
                      style={{ width: '100%', padding: '8px', borderRadius: 9, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.07)', color: 'rgba(255,255,255,.5)', fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', cursor: 'pointer', transition: '.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.06)'; e.currentTarget.style.color = '#14B8A6'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.03)'; e.currentTarget.style.color = 'rgba(255,255,255,.5)'; }}
                      onClick={() => {
                        try {
                          const allNotificationIds = notifications.map(n => n.id);
                          const newDismissed = new Set([...Array.from(dismissedNotifications), ...allNotificationIds]);
                          setDismissedNotifications(newDismissed);
                          localStorage.setItem('dismissedNotifications', JSON.stringify(Array.from(newDismissed)));
                          setReadNotifications(new Set());
                          localStorage.removeItem('readNotifications');
                          setNotificationKey(prev => prev + 1);
                          setShowNotifications(false);
                        } catch (error) {
                          console.error('Error clearing notifications:', error);
                        }
                      }}
                    >
                      CLEAR ALL
                    </button>
                  );
                })()}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
                {(() => {
                  const notifications = generateNotifications();
                  const accentFor = (p: string) => p === 'high' ? '#EF4444' : p === 'medium' ? '#F59E0B' : '#14B8A6';
                  return notifications.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {notifications.map((notification) => {
                        const isUnread = !readNotifications.has(notification.id);
                        const accent = accentFor(notification.priority);
                        return (
                          <div key={notification.id} style={{ position: 'relative', background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 12, padding: 13, opacity: isUnread ? 1 : 0.55 }}>
                            {/* Dismiss */}
                            <button
                              aria-label="Dismiss notification"
                              onClick={(e) => {
                                e.stopPropagation();
                                const next = new Set([...Array.from(dismissedNotifications), notification.id]);
                                setDismissedNotifications(next);
                                localStorage.setItem('dismissedNotifications', JSON.stringify(Array.from(next)));
                                setNotificationKey((k) => k + 1);
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.09)'; e.currentTarget.style.color = '#fff'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,.3)'; }}
                              style={{ position: 'absolute', top: 8, right: 8, width: 24, height: 24, borderRadius: 7, background: 'transparent', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,.3)', cursor: 'pointer', transition: '.15s' }}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
                              <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${accent}1a`, border: `1px solid ${accent}33`, color: accent }}>
                                {notification.type === 'upcoming' ? <Clock className="w-4 h-4" />
                                  : notification.type === 'info' ? <Info className="w-4 h-4" />
                                  : notification.type === 'tip' ? <CheckCircle className="w-4 h-4" />
                                  : <AlertCircle className="w-4 h-4" />}
                              </div>
                              <div style={{ flex: 1, minWidth: 0, paddingRight: 28 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                  {isUnread && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#14B8A6', flexShrink: 0, boxShadow: '0 0 6px rgba(20,184,166,.6)' }} />}
                                  <p style={{ fontSize: 13, fontWeight: 500, color: '#fff', lineHeight: 1.3 }}>{notification.title}</p>
                                </div>
                                <p style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginTop: 4, lineHeight: 1.5 }}>{notification.message}</p>
                                {notification.course && (
                                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: 'rgba(255,255,255,.4)' }}>
                                    <p>📍 {notification.location}</p>
                                    <p>👨‍🏫 {notification.instructor}</p>
                                    <p>⏰ {notification.time}</p>
                                  </div>
                                )}
                                <p style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.04em', color: 'rgba(255,255,255,.25)', marginTop: 8 }}>
                                  {notification.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ background: 'rgba(255,255,255,.03)', border: '1px dashed rgba(255,255,255,.1)', borderRadius: 12, textAlign: 'center', padding: '36px 20px' }}>
                      <Bell className="w-9 h-9" style={{ margin: '0 auto 12px', color: 'rgba(255,255,255,.15)' }} />
                      <p style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '.12em', color: 'rgba(255,255,255,.25)' }}>NO NEW NOTIFICATIONS</p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* User Profile Menu */}
        {showUserMenu && (
          <div className="fixed inset-0 sm:inset-auto sm:top-16 sm:right-4 z-50">
            {/* Mobile backdrop */}
            <div className="fixed inset-0 sm:hidden" style={{ background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(4px)' }} onClick={() => setShowUserMenu(false)} />

            <div
              data-panel="user-menu"
              onClick={(e) => e.stopPropagation()}
              className="fixed bottom-0 left-0 right-0 sm:relative sm:bottom-auto sm:left-auto sm:right-auto w-full sm:w-72 rounded-t-2xl sm:rounded-2xl overflow-hidden"
              style={{
                background: '#111', border: '1px solid rgba(255,255,255,.08)',
                boxShadow: '0 16px 50px rgba(0,0,0,.55)',
                fontFamily: "'Outfit',sans-serif", color: '#fff',
                paddingBottom: 'env(safe-area-inset-bottom,0px)',
              }}
            >
              <style>{`
                .um-row{display:flex;align-items:center;justify-content:space-between;width:100%;gap:10px;padding:11px 12px;border-radius:11px;background:transparent;border:1px solid transparent;cursor:pointer;transition:.15s;text-align:left;color:#fff;font-family:'Outfit',sans-serif}
                .um-row:hover{background:rgba(255,255,255,.05);border-color:rgba(255,255,255,.08)}
                .um-row.danger:hover{background:rgba(239,68,68,.08);border-color:rgba(239,68,68,.2)}
                .um-ic{width:34px;height:34px;border-radius:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0;background:rgba(20,184,166,.1);border:1px solid rgba(20,184,166,.2);color:#14B8A6}
              `}</style>

              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: 'rgba(20,184,166,.12)', border: '1px solid rgba(20,184,166,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#14B8A6', fontFamily: "'JetBrains Mono',monospace", fontSize: 16, fontWeight: 600 }}>
                  {userPreferences?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userPreferences?.name || 'Student'}</div>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.06em', color: 'rgba(255,255,255,.4)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[userPreferences?.program, userPreferences?.semester ? `SEM ${userPreferences.semester}` : null].filter(Boolean).join(' · ') || 'STUDENT'}
                  </div>
                </div>
                <button onClick={() => setShowUserMenu(false)} className="sm:hidden" style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,.5)', cursor: 'pointer', flexShrink: 0 }}>
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Items */}
              <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* Theme toggle */}
                <button type="button" className="um-row" onClick={(e) => { e.stopPropagation(); toggleTheme(); }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <span className="um-ic">{theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}</span>
                    <span style={{ fontSize: 13 }}>{theme === 'dark' ? 'Dark mode' : 'Light mode'}</span>
                  </span>
                  <span style={{ width: 40, height: 22, borderRadius: 11, flexShrink: 0, position: 'relative', transition: '.2s', background: theme === 'dark' ? 'rgba(20,184,166,.3)' : 'rgba(255,255,255,.1)' }}>
                    <span style={{ position: 'absolute', top: 3, left: theme === 'dark' ? 21 : 3, width: 16, height: 16, borderRadius: '50%', transition: '.2s', background: theme === 'dark' ? '#14B8A6' : 'rgba(255,255,255,.6)' }} />
                  </span>
                </button>

                {/* Settings */}
                <button type="button" className="um-row" onClick={(e) => { e.stopPropagation(); handleSettingsClick(); }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <span className="um-ic"><Settings className="w-4 h-4" /></span>
                    <span style={{ fontSize: 13 }}>Settings</span>
                  </span>
                </button>

                {/* Logout */}
                <button type="button" className="um-row danger" onClick={(e) => { e.stopPropagation(); handleLogout(); }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <span className="um-ic" style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.2)', color: '#EF4444' }}><LogOut className="w-4 h-4" /></span>
                    <span style={{ fontSize: 13, color: '#EF4444' }}>Logout</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          userPreferences={userPreferences}
        />

        {/* Mobile Install Banner */}
        <MobileInstallBanner />

        {/* Offline Indicator */}
        <OfflineIndicator />

        {/* Notification Permission Prompt */}
        <NotificationPermissionPrompt autoShow={true} />

        {/* Floating Chat Button - Draggable (hidden on the dedicated Owlet page) */}
        {!showChat && owletInitialized && !isOwletPage && (
          <div className="fixed z-50" style={{ left: owletPosition.x, top: owletPosition.y }}>
            {/* Tooltip */}
            {showOwletTooltip && (
              <div className="absolute bottom-full right-0 mb-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div
                  className="rounded-xl px-4 py-2 whitespace-nowrap"
                  style={{ background: '#141414', border: '1px solid rgba(255,255,255,.1)', boxShadow: '0 12px 30px rgba(0,0,0,.5)' }}
                >
                  <p className="text-sm" style={{ fontFamily: "'Outfit',sans-serif", color: '#fff', margin: 0 }}>Hi! I'm Owlet, here to help!</p>
                </div>
                <div
                  className="absolute bottom-0 right-6 translate-y-1/2 rotate-45 w-2 h-2"
                  style={{ background: '#141414', borderRight: '1px solid rgba(255,255,255,.1)', borderBottom: '1px solid rgba(255,255,255,.1)' }}
                />
              </div>
            )}

            <button
              ref={owletRef}
              onClick={() => { setShowOwletTooltip(false); !isDragging && setShowChat(true); }}
              onMouseDown={handleOwletMouseDown}
              onTouchStart={handleOwletMouseDown}
              className={`w-14 h-14 rounded-full flex items-center justify-center group touch-none transition-transform duration-150 ${isDragging ? 'cursor-grabbing scale-110' : 'cursor-grab hover:scale-110'}`}
              style={{
                background: '#111',
                border: '1px solid rgba(20,184,166,.3)',
                boxShadow: '0 10px 34px rgba(0,0,0,.55), 0 0 22px rgba(20,184,166,.18)',
              }}
              aria-label="Open Owlet Assistant (drag to move)"
            >
              <img src="/logo2.png" alt="Owlet" className="w-10 h-10 object-contain" />
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full animate-pulse" style={{ border: '2px solid #111' }} />
            </button>
          </div>
        )}

        {/* Chat Assistant Panel */}
        {showChat && (
          <div
            className="fixed inset-0 lg:inset-auto lg:bottom-6 lg:right-6 z-50 w-full lg:w-[420px] h-full lg:h-[85vh] lg:max-h-[700px] overflow-hidden lg:rounded-2xl animate-in fade-in-0 slide-in-from-bottom-4 duration-300"
            style={{
              background: '#0A0A0A', border: '1px solid rgba(255,255,255,.08)', boxShadow: '0 24px 70px rgba(0,0,0,.6)',
              paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            <ChatAssistant onClose={() => setShowChat(false)} />
          </div>
        )}
    </DashboardProvider>
  );
}
